<template>
  <div class="m-soccer-container tw-rounded-[20px]">
    <SectionNavs
      :sport-id="currentSportId"
      track-type="Pre"
      :sport-list="prematchSports"
      @change="onChangeSport"
    >
      <template slot="title">
        <!-- for seo -->
        <span style="display: none;">Sports</span>
        <img
          :src="sportsTitleIcon"
          alt="Sports"
          loading="lazy"
        >
      </template>
    </SectionNavs>

    <div
      v-if="prematchSports.length > 0"
      class="tab-nav-list"
      :class="[`column-${tabList.length}`]"
    >
      <button
        v-for="tab in tabList"
        :key="tab"
        class="nav-item"
        :class="{
          active: tab === currentTab
        }"
        @click="onChangeTab(tab)"
      >
        {{ tab }}
        <!-- soccer 不显示计数 -->
        <template v-if="tab === 'Today' && shouldShowTodayCount && todayMatchesCount >= 0">
          ({{ todayMatchesCount }})
        </template>
      </button>
    </div>

    <Empty v-else-if="loading === false" />

    <div v-block-loading:initFetch="loading">
      <template v-if="loading === false">
        <div class="tab-section">
          <MarketNavs
            v-if="!isTabCountries"
            v-model="currentMarketId"
            class="market-navs"
            :market-list="defaultMarkets"
            @change="onChangeMarket"
          />

          <keep-alive>
            <MatchCountries
              v-if="isTabCountries"
              :sport-id="currentSportId"
              :sport-name="currentSportName"
              :show-icon="false"
              :show-top-title="false"
              class="m-sports-countries"
            />
          </keep-alive>

          <Highlights
            v-if="currentTab === 'Highlights'"
            :proxyed="defaultTab === currentTab"
            :current-market-id="currentMarketId"
            :sport-id="currentSportId"
            :sport-name="currentSportName"
          />

          <Today
            v-else-if="currentTab === 'Today'"
            :proxyed="defaultTab === currentTab"
            :current-market-id="currentMarketId"
            :sport-id="currentSportId"
            :sport-name="currentSportName"
          />

          <Next7Days
            v-else-if="currentTab === 'Next 7 days'"
            :proxyed="defaultTab === currentTab"
            :current-market-id="currentMarketId"
            :sport-id="currentSportId"
            :sport-name="currentSportName"
          />

          <TopLeagues
            v-else-if="currentTab === 'Top Leagues'"
            :proxyed="defaultTab === currentTab"
            :current-market-id="currentMarketId"
            :sport-id="currentSportId"
            :sport-name="currentSportName"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { storeToRefs } from 'pinia'
import asyncComponent from '@ms/components/asyncComponent.js'
import { useSportsStore } from '@ms/stores/sports'
import useCurrentSport from '@ms/stores/sports/useCurrentSport.js'
import SectionNavs from '../modules/SectionNavs'
import MarketNavs from 'wap/match/MarketNavs'
import Highlights from '../modules/Highlights'
import Empty from 'wap/components/Empty'
import useDefaultMarket from 'composables/market/useDefaultMarket.js'
import Today from '../modules/Today'
import Next7Days from '../modules/Next7Days'
import TopLeagues from '../modules/TopLeagues'
import useHomeStore from 'wap/stores/home'
import { useThemeImage } from 'composables/useThemeImage.js'

export default {
  name: 'SportsContainer',
  components: {
    SectionNavs,
    Highlights,
    Today,
    Next7Days,
    TopLeagues,
    MatchCountries: asyncComponent(() => import(/* webpackPrefetch: true, webpackChunkName: 'match-countries' */ 'wap/match/MatchCountries.vue')),
    MarketNavs,
    Empty
  },
  setup() {
    const { prematchSports } = storeToRefs(useSportsStore())
    const { currentSportId, currentSportName, updateCurrentSportId } = useCurrentSport()

    const { defaultMarkets, getMarketInfo } = useDefaultMarket()

    const store = useHomeStore()
    const { todayMatchesCount, next7DaysMatchesCount } = storeToRefs(store)

    const sportsTitleIcon = useThemeImage({
      light: {
        default: require('../images/sports-light@2x.png')
      },
      dark: {
        default: require('../images/sports-dark@2x.png')
      }
    })

    return {
      prematchSports,
      defaultMarkets,
      getMarketInfo,
      currentSportId,
      currentSportName,
      updateCurrentSportId,
      next7DaysMatchesCount,
      todayMatchesCount,
      sportsTitleIcon
    }
  },
  data() {
    return {
      currentTab: '',
      // 初始化默认 tab, 用来告知 component 子组件是否发起请求
      defaultTab: '',
      currentMarketId: '',
      loading: true // 1 为 true, 0: false, -1: 加载失败
    }
  },
  computed: {
    shouldShowTodayCount() {
      return this.currentSportId !== 'sr:sport:1'
    },
    tabList() {
      const { currentSportId } = this
      if (currentSportId === 'sr:sport:1') {
        // 足球
        return ['Highlights', 'Today', 'Top Leagues', 'Countries']
      } else if (currentSportId === 'lv:sport:1') {
        // V- Soccer
        return ['Today', 'Countries']
      } else {
        // 其他运动
        return ['Next 7 days', 'Today', 'Countries']
      }
    },
    isTabCountries() {
      return this.currentTab === 'Countries'
    }
  },
  mounted() {
    const { tab, mId } = this.$route.query
    if (tab) {
      this.currentTab = decodeURIComponent(tab)
      this.defaultTab = this.currentTab
    }
    if (mId) {
      this.currentMarketId = mId
    }
    this.initFetch()
  },
  methods: {
    // 这个方法纯碎是为了一起获取 sports, markets, events, 方便初次加载 loading
    async initFetch() {
      try {
        const { sId } = this.$route.query
        if (sId && !this.currentSportId) {
          this.updateCurrentSportId(sId)
        }
        this.setSportId()
        this.resolveCurrentTab()
        if (!this.isTabCountries) {
          this.loading = true
          const reqList = [this.fetchMarketInfo()]
          reqList.push(this.fetchMatchList(this.currentSportId))
          await Promise.all(reqList)
        }
        this.loading = false
        if (this.shouldShowTodayCount) {
          useHomeStore().getTodayMatchesCount(this.currentSportId)
        }
      } catch (error) {
        if (error === 'canceled' || error.canceled) {
          if (this.isTabCountries) {
            this.loading = false
          }
          return
        }
        this.loading = -1
        console.error(error)
      }
    },
    setSportId() {
      // 检查 current sportId 是否有效
      const { prematchSports } = this
      if (prematchSports.length) {
        if (!this.currentSportId) {
          this.updateCurrentSportId(prematchSports[0].sportId)
        }
      }
    },
    resolveCurrentTab() {
      const { currentTab, tabList } = this
      if (currentTab) {
        const isValid = tabList.includes(currentTab)
        if (!isValid) {
          // 若无效，则重置为默认第一个
          this.currentTab = tabList[0]
          this.defaultTab = tabList[0]
        }
      } else {
        this.currentTab = tabList[0]
        this.defaultTab = tabList[0]
      }
    },
    // 获取 market 导航列表
    async fetchMarketInfo() {
      await this.getMarketInfo({ sportId: this.currentSportId })
      if (this.isTabCountries) {
        return
      }
      // 检查 currentMarketId 是否有效
      const { currentMarketId, defaultMarkets } = this
      if (currentMarketId) {
        const isValid = defaultMarkets.findIndex((x) => x.id === currentMarketId) !== -1
        if (!isValid) {
          // 若无效，则重置为默认第一个
          this.currentMarketId = defaultMarkets[0].id
        }
      } else {
        this.currentMarketId = defaultMarkets[0].id
      }
    },
    // 获取当前 tab 比赛列表
    fetchMatchList(sportId) {
      const store = useHomeStore()
      switch (this.currentTab) {
        case 'Highlights':
          return store.getHighlightMatches(sportId)
        case 'Today':
          return store.getTodayMatches(sportId)
        case 'Top Leagues':
          return store.getTopLeagues(sportId)
        case 'Next 7 days':
          return store.getNext7DaysMatches(sportId)
        default:
          // no default
          return ''
      }
    },
    onChangeSport(sportId) {
      const store = useHomeStore()
      store.resetTodayCount()
      store.resetNext7DaysCount()
      this.updateCurrentSportId(sportId)
      this.initFetch()

      const query = { ...this.$route.query }
      query.sId = sportId
      delete query.mId
      this.$router.replace({ query })
    },
    onChangeTab(tab) {
      if (this.currentTab === tab) return
      this.currentTab = tab
      // 清空 defaultTab, 目标 tab 需请求最新数据
      this.defaultTab = ''
      this.fetchMarketInfo()
      const encodeTab = encodeURIComponent(tab)
      const query = { ...this.$route.query }
      query.tab = encodeTab
      this.$router.replace({ query })
      window.mstrace(`Home_${encodeTab}`, 'Home')
    },
    onChangeMarket(marketId) {
      const query = { ...this.$route.query }
      query.mId = marketId
      this.$router.replace({ query })
    }
  }
}
</script>

<style lang="less">
@import url("~wap/common/style/variable.less");

.m-soccer-container {
  margin: 15px 0;

  .m-title {
    img {
      width: 84px;
    }
  }

  .nav-title {
    padding: 0 10px 0 20px;
  }

  .nav-wrap {
    padding: 0 0 0 12px;
  }

  .tab-nav-list {
    display: flex;
    height: 50px;
    align-items: center;
    padding: 0 20px;
    background: #1B1B1B;

    &.column-3,
    &.column-2 {
      justify-content: flex-start;

      .nav-item {
        margin-right: 20px;
      }
    }

    &.column-4 {
      justify-content: space-between;
    }

    .nav-item {
      color: #999;
      cursor: pointer;
      font-size: 14px;
      line-height: 18px;

      &.active {
        color: #ffca27;
        font-weight: 800;
      }
    }
  }

  .tabs-bar .tabs-nav {
    .tabs-tab {
      height: 36/@rem;
      flex: 1;
      font-size: 14/@rem;
      line-height: 36/@rem;

      &.tabs-tab-active {
        color: @primary;
      }

      &:active {
        background: fadeout(@mask, 10%);
      }
    }
  }
}
</style>

<style lang="scss" scoped>
.tab-section {
  .market-navs {
    height: 50px;
    background: #161616;

    ::v-deep {
      .m-sub-navs {
        background: none;
      }

      .snap-nav-item {
        height: 26px;
        line-height: 26px;
      }
    }
  }

  .m-sports-countries {
    padding-bottom: 20px;
    border-radius: 0 0 20px 20px;
    background: #161616;
    font-family: SFProText-Medium;
  }
}

[data-mode="light"] {
  .tab-section {
    .market-navs {
      background: #fff;

      ::v-deep {
        .m-sub-navs {
          background: none;
        }

        .snap-nav-item {
          background: #F7F7F7;
          color: #333;

          &.active {
            background-image: linear-gradient(-66deg, #FFE74F 0%, #FFAD31 100%);
            color: #000;
          }
        }
      }
    }
  }

  .tab-nav-list {
    background: rgba(247,247,247,50%);
  }

  .nav-item {
    color: #333;

    &.active {
      color: #000;
      font-weight: 18px;
    }
  }

  .m-sports-countries {
    background: #fff;
  }
}
</style>